import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Mixpanel } from '../services/Mixpanel';
import { getDaysUntilPremiumEnds } from '../utils/churnedOrg';
import { applyAdCopyOverride } from './adCopyOverride';
import { applyAdCopyTokens } from './adCopyTokens';
import { applyProgramCopy } from './applyProgramCopy';
import { recordFirstSeen, retireAds } from './curriculumAnnouncementNux';
import { persistAdDismissal } from './persistAdDismissal';
import { resolvePreviewAd } from './previewAd';
import { resolveAdFields } from './resolveAdFields';
import { selectAd } from './selectAd';
import { isSlotQuiet, quietSlot, SLOT_QUIET } from './slotQuiet';
import { useAdContext } from './useAdContext';

/**
 * One ad slot: pick the ad, hide it when dismissed, report the three ad events.
 *
 * The registry arrives as an ARGUMENT and is never imported here — same reason
 * `selectAd` takes one. A shared hook that reached for a surface's registry
 * itself would also step around the `vi.mock('../../../ads/adRegistry')` that the
 * export hook's suite substitutes a fake registry through.
 *
 * Quieting is this hook's job, not selectAd's: the slot knows which flavour it
 * paces itself with, so it makes both the check and the write.
 *
 * @param registry      ordered ad descriptors; first eligible rung wins
 * @param surface       Mixpanel discriminator — 'sidebar', 'export_modal', …
 * @param quietKey      this slot's own quiet key
 * @param quietFlavour  SLOT_QUIET.SESSION (per tab) or .DATED (a week)
 * @param standDown     caller-computed suppression (e.g. the sidebar's route rule)
 * @param hookName      labels the ?previewAd= warning with who asked
 * @param ctx           extra targeting terms only this surface can answer. Must
 *                      be JSON-serializable scalars: the effect depends on its
 *                      serialization, because an object literal rebuilt every
 *                      render would re-select every render.
 * @param track         the event sink. Defaults to Mixpanel; the in-resource
 *                      slot passes usePacketMixpanel's trackPacketEvent so its
 *                      events carry packet_id/title/source. Held in a ref and
 *                      kept OUT of the effect deps — usePacketMixpanel returns a
 *                      fresh function every render, so depending on it would
 *                      re-select forever.
 */
export function useAdSlot({
  registry,
  surface,
  quietKey,
  quietFlavour = SLOT_QUIET.SESSION,
  standDown = false,
  hookName,
  ctx = null,
  track: trackEvent = Mixpanel.track,
}) {
  const {
    diffitUser,
    setDiffitUser,
    isSignedIn,
    isLDidentified,
    isRealDiffitAdmin,
    inAppAdsCopy,
    referralAdCopy,
    expiringSoonWindowDays,
    seenNux,
    tier,
    countryCode,
    hasClaimedMathOffer,
    isReferralCampaignLive,
    isMathShareCampaignLive,
    isChromeExtensionAudience,
    isPremiumOfferPending,
    canAskAdminToRenew,
    canShareWithAdmin,
    canAskColleaguesToShare,
    offersExtensionDays,
    announcedCurriculumId,
    announcedCurriculumName,
    announcedCurriculumGrades,
    isCurriculumIndexPending,
  } = useAdContext();
  const { search } = useLocation();

  const previewAd = resolvePreviewAd({ registry, search, isRealDiffitAdmin, hookName });

  const trackRef = useRef(trackEvent);
  trackRef.current = trackEvent;

  // Depended on by value, not identity — see the `ctx` note above.
  const ctxKey = JSON.stringify(ctx ?? null);

  const [ad, setAd] = useState(null);
  const [isResolved, setIsResolved] = useState(false);
  const [dismissedId, setDismissedId] = useState(null);

  useEffect(() => {
    // Also what a descriptor's function-valued fields are resolved against.
    const adCtx = {
      diffitUser,
      isLDidentified,
      tier,
      countryCode,
      isReferralCampaignLive,
      isMathShareCampaignLive,
      isChromeExtensionAudience,
      hasClaimedMathOffer,
      canAskAdminToRenew,
      canShareWithAdmin,
      canAskColleaguesToShare,
      offersExtensionDays,
      expiringSoonWindowDays,
      announcedCurriculumId,
      announcedCurriculumName,
      announcedCurriculumGrades,
      ...ctx,
    };

    if (previewAd) {
      setAd(resolveAdFields(previewAd, adCtx));
      setIsResolved(true);
      return undefined;
    }

    setAd(null);

    if (!isSignedIn || standDown || isSlotQuiet(quietFlavour, quietKey)) {
      setIsResolved(true);
      return undefined;
    }
    // The premium offer is waited on like LD identify, not treated as "no": the expiry
    // campaigns outrank everything else, so letting a lower rung paint while the lookup is in
    // flight would swap the card out from under the teacher a moment later. Only pending for
    // a teacher one of those campaigns could actually reach — see useAdContext.
    if (!diffitUser || !isLDidentified || isPremiumOfferPending || isCurriculumIndexPending) {
      setIsResolved(false);
      return undefined;
    }

    let cancelled = false;
    setIsResolved(false);
    selectAd(registry, adCtx, { seenNux }).then((next) => {
      if (cancelled) return;
      setAd(resolveAdFields(next, adCtx));
      setIsResolved(true);
    });

    return () => {
      cancelled = true;
    };
  }, [
    registry,
    quietKey,
    quietFlavour,
    ctxKey,
    previewAd,
    isSignedIn,
    standDown,
    diffitUser,
    isLDidentified,
    tier,
    countryCode,
    isReferralCampaignLive,
    isMathShareCampaignLive,
    isChromeExtensionAudience,
    hasClaimedMathOffer,
    isPremiumOfferPending,
    canAskAdminToRenew,
    canShareWithAdmin,
    canAskColleaguesToShare,
    offersExtensionDays,
    expiringSoonWindowDays,
    announcedCurriculumId,
    announcedCurriculumName,
    announcedCurriculumGrades,
    isCurriculumIndexPending,
    seenNux,
  ]);

  const winner = ad && ad.id !== dismissedId ? ad : null;

  // Applied to the winner rather than to the registry, so the LaunchDarkly copy
  // also reaches the `?previewAd=` descriptor above — the one way to look at an
  // edit before the campaign's audience does. Selection is deliberately upstream
  // of it: rewording a card must not change which card is shown.
  // Tokens fill AFTER the override, so an operator rewording an expiry card in LaunchDarkly
  // can keep `{days}` in the new wording and still get the teacher's own number.
  const daysUntilPremiumEnds = getDaysUntilPremiumEnds(diffitUser);
  // Registry default (function fields already resolved) -> program copy -> LaunchDarkly -> tokens.
  // Held back while the program copy is in flight so the generic wording never paints and then
  // swaps. A `?previewAd=` of a campaign that is not live never loads the copy, so it must not wait
  // on it.
  const isAwaitingProgramCopy =
    !!winner?.programCopy && isReferralCampaignLive && referralAdCopy === undefined;
  const visibleAd = useMemo(
    () =>
      isAwaitingProgramCopy
        ? null
        : applyAdCopyTokens(
            applyAdCopyOverride(applyProgramCopy(winner, referralAdCopy), inAppAdsCopy),
            daysUntilPremiumEnds
          ),
    [winner, referralAdCopy, isAwaitingProgramCopy, inAppAdsCopy, daysUntilPremiumEnds]
  );

  // `surface` is what separates this slot's events from the other slots' in
  // Mixpanel. Without it, `In-App Ad Shown` with the same ad_id is
  // indistinguishable across surfaces — and it is also what separates the two
  // export modals, which share a registry.
  const track = (event, props) => {
    if (!visibleAd) return;
    trackRef.current(event, { ad_id: visibleAd.id, surface, ...props });
  };

  const dismiss = () => {
    if (!visibleAd) return;
    track('In-App Ad Dismissed');
    setDismissedId(visibleAd.id);
    quietSlot(quietFlavour, quietKey);

    persistAdDismissal({ diffitUser, setDiffitUser, adId: visibleAd.id });
  };

  return {
    ad: visibleAd,
    isResolved: isResolved && !isAwaitingProgramCopy,
    dismiss,
    // `firstSeenKey` starts a timed campaign's clock; `retireOnClick` ends an ad whose click is
    // the whole ask. Neither counts a reviewer's `?previewAd=` look.
    trackShown: () => {
      track('In-App Ad Shown');
      if (visibleAd?.firstSeenKey && !previewAd)
        recordFirstSeen(diffitUser, visibleAd.firstSeenKey);
    },
    trackClick: ({ promptLabel } = {}) => {
      track('In-App Ad Clicked', promptLabel ? { prompt_label: promptLabel } : undefined);
      if (visibleAd?.retireOnClick && !previewAd) retireAds(diffitUser, visibleAd.id);
    },
    onModalTrack: (event, props) => trackRef.current(event, { ...props, surface }),
  };
}
